import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Loader, GripVertical, Clock, Calendar, Flag, MoreVertical, Trash2, Edit3 } from 'lucide-react';
import api from '../utils/api';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { formatEffortFromMinutes } from '../utils/effortFormatting';

// Draggable Task Card Component
const DraggableTaskCard = ({ task, onEdit, onDelete }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'PERSONAL_TASK',
    item: { taskId: task.id, sourceStageId: task.personal_stage_id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div
      ref={drag}
      className={`bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm hover:shadow-md transition-all cursor-move group ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 hover:text-purple-600 cursor-pointer" onClick={() => onEdit(task)}>
            {task.name}
          </h4>
        </div>
        <button
          onClick={() => onDelete(task.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
        >
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      </div>

      {task.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {task.priority && (
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
            {task.priority}
          </span>
        )}
        
        {task.due_date && (
          <div className="flex items-center text-xs text-gray-600">
            <Calendar className="w-3 h-3 mr-1" />
            {new Date(task.due_date).toLocaleDateString()}
          </div>
        )}
        
        {task.estimated_effort && (
          <div className="flex items-center text-xs text-gray-600">
            <Clock className="w-3 h-3 mr-1" />
            {task.estimated_effort}
          </div>
        )}
      </div>
    </div>
  );
};

// Droppable Stage Column Component
const DroppableStageColumn = ({ stage, tasks, onTaskDrop, onEdit, onDelete, onAddTask }) => {
  const [{ isOver }, drop] = useDrop({
    accept: 'PERSONAL_TASK',
    drop: (item) => onTaskDrop(item.taskId, stage.id),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop}
      className={`flex-shrink-0 w-80 bg-gray-50 rounded-lg p-4 ${
        isOver ? 'ring-2 ring-purple-500 bg-purple-50' : ''
      }`}
    >
      {/* Stage Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: stage.color }}
          />
          <h3 className="font-semibold text-gray-900">{stage.name}</h3>
          <span className="text-sm text-gray-500">({tasks.length})</span>
        </div>
        <button
          onClick={() => onAddTask(stage)}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          title="Add task"
        >
          <Plus className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Tasks */}
      <div className="space-y-3 min-h-[400px]">
        {tasks.map((task) => (
          <DraggableTaskCard
            key={task.id}
            task={task}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
        
        {tasks.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            No tasks in this stage
          </div>
        )}
      </div>
    </div>
  );
};

const PersonalTasksKanban = ({ user, onSettingsClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [tasksByStage, setTasksByStage] = useState({});
  const [selectedStage, setSelectedStage] = useState(null);

  useEffect(() => {
    loadKanbanData();
  }, []);

  const loadKanbanData = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/personal-tasks/kanban');
      
      // Parse response into stages and tasks
      const stagesData = response.data.map(item => item.stage);
      const tasksMap = {};
      
      response.data.forEach(item => {
        tasksMap[item.stage.id] = item.tasks;
      });
      
      setStages(stagesData);
      setTasksByStage(tasksMap);
    } catch (error) {
      console.error('Error loading kanban data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskDrop = async (taskId, targetStageId) => {
    try {
      await api.put(`/personal-tasks/${taskId}/move-stage`, { stage_id: targetStageId });
      
      // Reload data
      await loadKanbanData();
    } catch (error) {
      console.error('Error moving task:', error);
      alert('Failed to move task');
    }
  };

  const handleEditTask = (task) => {
    // Navigate to task URL for consistent behavior
    navigate(`/tasks/${task.id}`);
  };

  const handleAddTask = (stage) => {
    // For new tasks, we could navigate to a special URL or use a different approach
    // For now, we'll create a quick task via API first and then navigate
    setSelectedStage(stage);
    createAndNavigateToNewTask(stage);
  };
  
  const createAndNavigateToNewTask = async (stage) => {
    try {
      const newTaskData = {
        name: 'New Task',
        is_personal: true,
        personal_stage_id: stage?.id
      };
      
      const response = await api.post('/tasks', newTaskData);
      
      if (response.data?.task?.id) {
        // Navigate to the new task
        navigate(`/tasks/${response.data.task.id}`);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await api.delete(`/tasks/${taskId}`);
      
      await loadKanbanData();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Personal Tasks</h2>
            <p className="text-sm text-gray-600 mt-1">Manage your personal tasks with a kanban board</p>
          </div>
          <button
            onClick={onSettingsClick}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </button>
        </div>

        {/* Kanban Board */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => (
            <DroppableStageColumn
              key={stage.id}
              stage={stage}
              tasks={tasksByStage[stage.id] || []}
              onTaskDrop={handleTaskDrop}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              onAddTask={handleAddTask}
            />
          ))}
        </div>
      </div>
    </DndProvider>
  );
};

export default PersonalTasksKanban;
